'use client';

import { c as _c } from "react/compiler-runtime";
import { Alert, Button, FormControl, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { PrivateLinkInstructionStep } from '@repo/api-gw-sdk';
import { PrivateLinkCheckResultStatusEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { CopyableCode } from '@/components/shared/copyableCode';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import { CheckingIndicator, stepFooterButtons, type StepDialogFadeProps } from './stepCheckControls';
import { isPrivateLinkSetupGraceActive, markPrivateLinkSetupStarted, useRdsCdcStatus } from '../useRdsCdcStatus';
type InstructionFormat = 'cloudformation' | 'aws-cli';
const RDS_SG_PLACEHOLDER = '<RDS_SECURITY_GROUP_ID>';
const isUrl = (value: string) => value.startsWith('https://');

// The CloudFormation quick link carries the placeholder URL-encoded, so both forms must be
// substituted or the console opens with an unresolved parameter.
const substituteSecurityGroup = (command: string, securityGroupId: string) => securityGroupId ? command.replaceAll(RDS_SG_PLACEHOLDER, securityGroupId).replaceAll(encodeURIComponent(RDS_SG_PLACEHOLDER), encodeURIComponent(securityGroupId)) : command;
const InstructionStep = t0 => {
  const $ = _c(24);
  const {
    step,
    securityGroupId,
    codeTitle
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== codeTitle || $[1] !== securityGroupId || $[2] !== step.command || $[3] !== step.description || $[4] !== step.title) {
    const command = substituteSecurityGroup(step.command ?? "", securityGroupId);
    T0 = Stack;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        gap: "8px"
      };
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    if ($[11] !== step.title) {
      t2 = <Typography variant="subtitle1Strong">{step.title}</Typography>;
      $[11] = step.title;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    if ($[13] !== step.description) {
      t3 = <Typography variant="body1">{step.description}</Typography>;
      $[13] = step.description;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t4 = !!command && !isUrl(command) && <CopyableCode code={command} title={codeTitle} codeClassName="overflow-y-auto max-h-[30vh]" />;
    $[0] = codeTitle;
    $[1] = securityGroupId;
    $[2] = step.command;
    $[3] = step.description;
    $[4] = step.title;
    $[5] = T0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    T0 = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[15] !== step.downloadLink) {
    t5 = !!step.downloadLink && <ButtonWithIcon variant="outlined" icon="material-symbols-download-rounded" iconPosition="right" text="Download CloudFormation Template" onClick={() => window.open(step.downloadLink, "_blank", "noopener")} sx={{
      alignSelf: "flex-start"
    }} />;
    $[15] = step.downloadLink;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== T0 || $[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <T0 sx={t1}>{t2}{t3}{t4}{t5}</T0>;
    $[17] = T0;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
};
export const PrivateLinkDialog = ({
  resourceId,
  onBack,
  onClose,
  ...fadeProps
}: {
  resourceId: string;
  onBack: () => void;
  onClose: () => void;
} & StepDialogFadeProps) => {
  const dal = useDAL();
  const {
    snackbar
  } = useWorkspace();
  const {
    isEmployee
  } = useUser();
  const [format, setFormat] = useState<InstructionFormat>('cloudformation');
  const [selectedSecurityGroupId, setSelectedSecurityGroupId] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isTearingDown, setIsTearingDown] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const {
    data: prefResponse
  } = dal.preferences.useUserPref(PreferencesKey.Preferences);
  const isDevMode = isEmployee && (prefResponse?.value?.isDevMode ?? false);
  const {
    data,
    isLoading,
    error
  } = dal.inventory.privateLink.useOnboardRdsCdc(resourceId);
  const {
    refresh,
    isPrivateLinkProvisioning,
    isPrivateLinkTearingDown,
    isPrivateLinkStepDone
  } = useRdsCdcStatus(resourceId);
  const securityGroups = data?.rdsSecurityGroups ?? [];
  const securityGroupId = selectedSecurityGroupId || securityGroups[0]?.id || '';
  const steps = data?.privateLinkInstructions?.[format]?.steps ?? [];
  const consoleLinkStep = steps.find(step => isUrl(step.command ?? ''));

  // setupAwsPrivateLink is not idempotent — it starts a fresh workflow on every call and the workflow
  // id is timestamped, so Temporal cannot dedupe. Only fire it when nothing is set up yet; otherwise
  // this button is a plain re-check.
  const runCheck = async (continueWhenChecked: boolean) => {
    setIsChecking(true);
    setCheckError(null);
    try {
      const latest = await refresh();
      const ramShareArn = latest?.ramShareCheck?.ramResourceShareArn;
      if (!latest?.ramShareCheck?.found || !ramShareArn) {
        setCheckError("We couldn't find the RAM resource share yet. Complete the steps above, then check again.");
        return;
      }
      if (latest.privateLinkCheck?.status === PrivateLinkCheckResultStatusEnum.NotSetup && !isPrivateLinkSetupGraceActive(resourceId)) {
        await dal.inventory.privateLink.setupAwsPrivateLink(resourceId, {
          ramResourceShareArn: ramShareArn
        });
        markPrivateLinkSetupStarted(resourceId);
        await refresh();
      }
      if (continueWhenChecked) {
        onBack();
      }
    } catch (setupError) {
      setCheckError(await extractErrorMessage(setupError));
    } finally {
      setIsChecking(false);
    }
  };
  const teardownPrivateLink = async () => {
    setIsTearingDown(true);
    try {
      const response = await dal.inventory.privateLink.teardownAwsPrivateLink(resourceId);
      snackbar.showMessage(`PrivateLink teardown started. Workflow ID: ${response.workflowId}`);
      await refresh();
    } catch (teardownError) {
      snackbar.showErrorMessage(await extractErrorMessage(teardownError));
    } finally {
      setIsTearingDown(false);
    }
  };
  return <MainDialog isLoading={isLoading} scrollableBody {...fadeProps} header={{
    title: 'Configure AWS PrivateLink',
    subtitle: <Stack direction='row' sx={{
      gap: '24px',
      alignItems: 'flex-start',
      paddingRight: '24px'
    }}>
            <Typography variant='body1' sx={{
        flex: 1
      }}>
              Create the PrivateLink resources in your AWS account so Eon can
              reach this database.
            </Typography>
            <ToggleButtonGroup value={format} exclusive size='small' onChange={(_, value: InstructionFormat | null) => {
        if (value !== null) {
          setFormat(value);
        }
      }}>
              <ToggleButton value='cloudformation'>CloudFormation</ToggleButton>
              <ToggleButton value='aws-cli'>Bash</ToggleButton>
            </ToggleButtonGroup>
          </Stack>,
    onBack,
    onClose
  }} footer={{
    // Not gated on the security group: the check verifies the RAM share and starts the
    // PrivateLink setup, neither of which takes the SG — it only parameterizes the
    // instructions and the console link.
    ...stepFooterButtons({
      isDone: isPrivateLinkStepDone,
      isBusy: isChecking,
      testIdPrefix: 'rds-cdc-privatelink',
      onContinue: onBack,
      onCheckAndContinue: () => void runCheck(true),
      onVerifyAgain: () => void runCheck(false)
    }),
    children: <Stack direction='row' sx={{
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap'
    }}>
            {!!consoleLinkStep && <ButtonWithIcon variant='text' icon='material-symbols-open-in-new' iconPosition='right' text='Open in AWS Console' disabled={!securityGroupId} disabledTooltip='Select a database security group to continue' onClick={() => window.open(substituteSecurityGroup(consoleLinkStep.command ?? '', securityGroupId), '_blank', 'noopener')} />}
            {isDevMode && <Button variant='outlined' color='error' disabled={isTearingDown} onClick={() => void teardownPrivateLink()}>
                {isTearingDown ? 'Tearing down...' : isPrivateLinkTearingDown ? 'Retry Teardown' : 'Teardown PrivateLink'}
              </Button>}
          </Stack>
  }}>
      <Stack sx={{
      gap: '24px',
      paddingX: '40px'
    }}>
        {!!error && <Alert severity='error'>
            Failed to load onboarding instructions. Please try again.
          </Alert>}

        {!isChecking && isPrivateLinkProvisioning && <Alert severity='info'>
            Eon is provisioning the connection to your database. This might take
            a few minutes.
          </Alert>}

        {securityGroups.length > 0 && <Stack sx={{
        gap: '4px'
      }}>
            <Typography variant='subtitle2'>Security Group</Typography>
            <FormControl size='small' sx={{
          maxWidth: '400px'
        }}>
              <Select value={securityGroupId} onChange={event => setSelectedSecurityGroupId(event.target.value)}>
                {securityGroups.map(securityGroup => <MenuItem key={securityGroup.id} value={securityGroup.id}>
                    {securityGroup.name} ({securityGroup.id})
                  </MenuItem>)}
              </Select>
            </FormControl>
            <Typography variant='body1'>
              Select the security group to allow traffic from the resource
              gateway.
            </Typography>
          </Stack>}

        {steps.filter(step_0 => !isUrl(step_0.command ?? '')).map(step_1 => <InstructionStep key={step_1.stepNumber} step={step_1} securityGroupId={securityGroupId} codeTitle={format === 'cloudformation' ? 'eon-privatelink-rds.yml' : 'setup-privatelink.sh'} />)}

        {isChecking && <CheckingIndicator text='Checking the PrivateLink status. This might take a few seconds.' />}
        {!isChecking && isPrivateLinkStepDone && <Alert severity='success'>
            PrivateLink is active. Re-running these steps is not needed.
          </Alert>}
        {!isChecking && !!checkError && <Alert severity='error'>{checkError}</Alert>}
      </Stack>
    </MainDialog>;
};